import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useWebKeyboard from '@/feature/responsible-gaming/hooks/use-web-keyboard';
import {
    formatCommaSeparatedNumber,
    getNextValueOnBackspacePress,
    getNextValueOnDecimalPress,
    getNextValueOnNumberPress,
} from '@/utils/currency-num-pad-utils';

/**
 * Custom hook for handling currency input values with the number pad
 * @param {number} initialValue - optional initial value of the input
 * @param {number} charLimit - max limit of the input
 * @returns {Object} - object containing the display value, number value, and numpad functions
 */
export const useCurrencyNumberPad = (initialValue?: number, charLimit = 10) => {
    const [inputValue, setInputValue] = useState(initialValue ? String(initialValue) : '0');
    const [pristine, setPristine] = useState(true);

    // refs to stabilize callbacks - prevents re-creating callbacks on every keystroke
    const inputValueRef = useRef(inputValue);
    inputValueRef.current = inputValue;
    const pristineRef = useRef(pristine);
    pristineRef.current = pristine;
    const charLimitRef = useRef(charLimit);
    charLimitRef.current = charLimit;

    useEffect(() => {
        const nextValue = initialValue ? String(initialValue) : '0';

        // update value only if it has changed to prevent unnecessary re-renders
        if (nextValue !== inputValueRef.current) {
            inputValueRef.current = nextValue;
            setInputValue(nextValue);
        }
    }, [initialValue]);

    const handleNextValue = useCallback((nextValue: string) => {
        if (pristineRef.current) {
            setPristine(false);
        }
        if (nextValue !== inputValueRef.current) {
            inputValueRef.current = nextValue;
            setInputValue(nextValue);
        }
    }, []);

    const onNumberPress = useCallback(
        (value: number) => {
            const next = getNextValueOnNumberPress(inputValueRef.current, value, charLimitRef.current);
            handleNextValue(next);
            return next;
        },
        [handleNextValue]
    );

    const onDecimalPress = useCallback(() => {
        const next = getNextValueOnDecimalPress(inputValueRef.current, '.');
        handleNextValue(next);
        return next;
    }, [handleNextValue]);

    const onBackspacePress = useCallback(() => {
        const next = getNextValueOnBackspacePress(inputValueRef.current);
        handleNextValue(next);
        return next;
    }, [handleNextValue]);

    const displayValue = useMemo(() => {
        return formatCommaSeparatedNumber(inputValue);
    }, [inputValue]);

    useWebKeyboard({ onNumberPress, onDecimalPress, onBackspacePress });

    return {
        displayValue,
        number: Number(inputValue),
        onNumberPress,
        onDecimalPress,
        onBackspacePress,
        setInputValue,
        pristine,
    };
};
